// FleetCostsPage — what each instance cost metricsIQ to run, day by day.
//
// A metricsIQ screen, and deliberately not a column on the customer's Fleet
// Management page: the cost of running an instance is our own book, posted by
// the instance from the platform's cost ledger, and a customer is never shown
// it. It reads GET /portal/api/metricsiq/fleet-costs/, which answers a staff
// token and 404s everybody else.
//
// ONE RANGE DRIVES THE WHOLE SCREEN. The control at the top names a run of
// calendar days — the month to date until somebody asks — and the total beside
// each instance, the day-by-day drill-down beneath it and the CSV export are
// all cut on that range, from the same two query parameters. Nothing here is
// relative to today behind the operator's back: the figure beside an instance
// is the sum of the days listed under it, and the export's rows sum to the
// same figure.
//
// THE OTHER RULE THIS SCREEN EXISTS TO KEEP: nothing posted is never 0. An
// instance posts a day once it has closed; a day it has not posted has no
// figure, and this screen says "Not posted" — for the day, and for a range in
// which no day was posted (an em dash, with "0 of n days posted" beside it). A
// day the instance DID post with no cost in it is a different fact and reads
// "No cost that day". The service keeps the two apart on the wire (`posted`
// on every day, `usd: null` on a range with nothing in it), so this screen
// never infers absence; it draws what it was told.
//
// Design-system components by module path, as every page here, and DS button
// handlers are `onPress`. Styling is role tokens only.

import { useCallback, useState } from 'react';
import { ChevronRight, Download } from 'lucide-react';
import { Button } from '@miq/design-system/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import type { AuthorizedFetch } from '../auth/api';
import { PortalRequestFailed } from '../auth/api';
import { usePortalSession } from '../auth/context';
import { DayRangePicker } from '../components/DayRangePicker';
import { ShowFormerToggle } from '../components/ShowFormerToggle';
import { StatusCell } from '../components/StatusCell';
import {
  downloadFleetCostsExport,
  readFleetCosts,
  type DayRangeQuery,
  type FleetCostDay,
  type FleetCostInstance,
  type FleetCostLine,
  type FleetCostWindow,
  type FleetCostsScreen,
} from '../data/acrossCustomers';
import { ABSENT, countText, dayLabel, dayRangeLabel, monthLabel, percentText, quantityText } from '../data/format';
import { money } from '../format';
import { when } from '../data/instants';
import { usePortalRead } from '../data/usePortalRead';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'Fleet Costs';

export function FleetCostsPage() {
  useDocumentTitle(TITLE);
  // `null` means "did not ask", which the service reads as the month to date —
  // see DayRangePicker for why the control keeps it that way.
  const [range, setRange] = useState<DayRangeQuery | null>(null);
  // Retired instances — an instance's only way of being former — are hidden by
  // default and come back under the toggle, memoised alongside the range for
  // the same reason the range is.
  const [showFormer, setShowFormer] = useState(false);

  const read = useCallback(
    (authorizedFetch: AuthorizedFetch) => readFleetCosts(authorizedFetch, range, showFormer),
    [range, showFormer],
  );
  const screen = usePortalRead(read);
  // What the toggle governs: the rows the default hid, or — once shown — the
  // retired rows on the screen. Zero while the screen is still loading, which
  // leaves a toggle that is on where it was and draws nothing otherwise.
  const hiddenCount =
    screen.status !== 'ready'
      ? 0
      : showFormer
        ? screen.data.instances.filter((row) => row.retired !== null).length
        : screen.data.former_hidden;

  // The header and the range control are rendered in every state, and not
  // only around a loaded screen: a range the service refuses — a typed date
  // after today, a `from` after its `to` — is answered 400, and the control
  // that produced it has to stay on screen for the operator to correct it. A
  // whole-screen failure here would leave them a message and a reload.
  return (
    <div className="space-y-5 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Commerce · metricsIQ
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{TITLE}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            What each instance cost metricsIQ to run, as each instance posts it
            from the platform&apos;s cost ledger — our own figure, shown to
            nobody outside metricsIQ. The total, the day-by-day drill-down and
            the export are all for the range chosen here. A day an instance has
            not posted is not posted; it is never a zero.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <DayRangePicker
            selected={range}
            onSelect={setRange}
            range={screen.status === 'ready' ? screen.data.range : null}
          />
          {/* A retired instance's cost is history and stays readable; it is
              not a running instance and is not listed as one by default. The
              export follows the toggle, so what is downloaded is what is
              shown. */}
          <ShowFormerToggle showing={showFormer} hiddenCount={hiddenCount} onChange={setShowFormer} of="instances" />
        </div>
      </div>

      {screen.status === 'loading' && (
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-muted-foreground" role="status">
              Loading…
            </p>
          </CardContent>
        </Card>
      )}
      {screen.status === 'failed' && (
        // The service's own wording, shown as-is — including the sentence a
        // refused range carries, and the 404 the endpoint answers a caller who
        // may not look, which is a refusal and not a missing page.
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-muted-foreground" role="alert">
              {screen.message}
            </p>
          </CardContent>
        </Card>
      )}
      {screen.status === 'ready' && (
        <FleetCosts screen={screen.data} range={range} showFormer={showFormer} />
      )}
    </div>
  );
}

function FleetCosts({
  screen,
  range,
  showFormer,
}: {
  screen: FleetCostsScreen;
  range: DayRangeQuery | null;
  showFormer: boolean;
}) {
  const { instances } = screen;
  const rangeLabel = dayRangeLabel(screen.range);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">Instances</CardTitle>
          <CardDescription>
            {instances.length === 1 ? '1 instance' : `${instances.length} instances`} ·
            the total, the drill-down and the export are all for {rangeLabel}.
          </CardDescription>
        </div>
        <ExportButton range={range} showFormer={showFormer} rangeLabel={rangeLabel} />
      </CardHeader>
      <CardContent className="p-0">
        {instances.length === 0 ? (
          // An estate with nothing registered is a LOADED screen with
          // nothing in it, which is the state a fresh deployment is in.
          <p className="px-6 py-8 text-sm text-muted-foreground">
            No instances are registered.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Instance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Reconciliation</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Days</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.map((row) => (
                  <InstanceRows key={row.instance.public_id} row={row} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * One instance's line, and its day-by-day drill-down beneath when opened.
 *
 * Two table rows from one component so the drill-down spans the table rather
 * than squeezing into a cell. Closed by default: the table is the screen, and
 * a month of days for every instance would push the next instance off it.
 */
function InstanceRows({ row }: { row: FleetCostInstance }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="font-medium">{row.instance.name}</div>
          <div className="font-mono text-xs text-muted-foreground">
            {row.instance.client_id ?? ABSENT}
          </div>
          <div className="text-xs text-muted-foreground">{row.instance.customer}</div>
        </TableCell>
        <TableCell>
          {/* Active or Retired only: an instance cannot be superseded, so the
              same cell the customer lists draw has no third answer here. */}
          <StatusCell retired={row.retired} supersededBy={null} />
        </TableCell>
        <TableCell className="text-right">
          <WindowCell window={row.usd} />
        </TableCell>
        <TableCell className="text-right">
          <ReconciliationCell reconciliation={row.reconciliation} />
        </TableCell>
        <TableCell>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={open}
            aria-label={open ? `Hide ${row.instance.name}'s days` : `Show ${row.instance.name}'s days`}
            onPress={() => setOpen((was) => !was)}
          >
            <ChevronRight className={`size-4 transition-transform ${open ? 'rotate-90' : ''}`} />
          </Button>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-muted/40 hover:bg-muted/40">
          <TableCell colSpan={5} className="p-0">
            <DayList days={row.days} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * The range's figure with how much of the range it rests on.
 *
 * The dash is for a range NOTHING was posted in. It is not a zero and does
 * not sit where one would: a posted day with no cost in it gives the range a
 * real `"0.00000000"`, which is drawn as $0.00 with "1 of 7 days posted"
 * beside it — a figure, resting on a day that was accounted for.
 */
function WindowCell({ window }: { window: FleetCostWindow }) {
  const posted = `${window.days_posted} of ${window.days_in_window} ${
    window.days_in_window === 1 ? 'day' : 'days'
  } posted`;
  return (
    <div className="tabular-nums">
      {window.usd === null ? (
        <div className="text-muted-foreground" title="Nothing was posted for these days">
          {ABSENT}
        </div>
      ) : (
        <div className="font-medium">{money(window.usd, 'USD')}</div>
      )}
      <div className="text-xs text-muted-foreground">{posted}</div>
    </div>
  );
}

/**
 * The reconciliation ratio for every month the range touches, newest first.
 *
 * One line per month rather than one figure, because a range that crosses a
 * month edge has two ledgers to reconcile against two bills, and showing only
 * the newer would be a claim about the older. Each ratio is as the newest
 * posted day of that month inside the range carried it; a month with no
 * posted day in the range, or whose newest day carried no run, reads as
 * absent with the month named.
 */
function ReconciliationCell({
  reconciliation,
}: {
  reconciliation: FleetCostInstance['reconciliation'];
}) {
  const months = Object.keys(reconciliation).sort().reverse();
  return (
    <div className="space-y-1 tabular-nums">
      {months.map((month) => {
        const ratio = reconciliation[month];
        const monthName = monthLabel(`${month}-01T00:00:00Z`);
        return (
          <div key={month}>
            {ratio === null ? (
              <div className="text-muted-foreground" title={`No reconciliation run for ${monthName}`}>
                {monthName} {ABSENT}
              </div>
            ) : (
              <>
                <div className="font-medium">
                  {monthName} {quantityText(ratio.ratio)}
                </div>
                <div className="text-xs text-muted-foreground">as of {dayLabel(ratio.day)}</div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The range, one entry per calendar day, newest first.
 *
 * Newest first because that is the day an operator opened the screen to check
 * on; the service lists oldest first for the export, which a spreadsheet
 * sorts itself. Every day of the range is here whether or not it was posted —
 * the service sends the calendar, so an unposted day is a row that says so
 * rather than a gap somebody has to notice.
 */
function DayList({ days }: { days: FleetCostDay[] }) {
  return (
    <div className="divide-y divide-border px-6 py-2">
      {[...days].reverse().map((day) => (
        <DayEntry key={day.day} day={day} />
      ))}
    </div>
  );
}

function DayEntry({ day }: { day: FleetCostDay }) {
  if (!day.posted) {
    return (
      <div
        className="flex items-baseline justify-between gap-4 py-2 text-sm"
        title={`Nothing was posted for ${dayLabel(day.day)}`}
      >
        <span className="text-muted-foreground">{dayLabel(day.day)}</span>
        <span className="text-muted-foreground">Not posted</span>
      </div>
    );
  }
  if (day.lines.length === 0) {
    return (
      <div className="flex items-baseline justify-between gap-4 py-2 text-sm">
        <span>{dayLabel(day.day)}</span>
        <span className="text-muted-foreground" title={`Posted ${when(day.posted_at)}`}>
          No cost that day
        </span>
      </div>
    );
  }
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className="font-medium">{dayLabel(day.day)}</span>
        <span className="tabular-nums" title={`Posted ${when(day.posted_at)}`}>
          <span className="font-medium">{money(day.usd, 'USD')}</span>
        </span>
      </div>
      <LineTable lines={day.lines} />
    </div>
  );
}

/** A day's lines by model and workload, with the four token kinds. */
function LineTable({ lines }: { lines: FleetCostLine[] }) {
  return (
    <div className="mt-1 overflow-x-auto">
      <Table className="text-xs">
        <TableHeader>
          <TableRow>
            <TableHead>Model</TableHead>
            <TableHead>Workload</TableHead>
            <TableHead className="text-right">Uncached input</TableHead>
            <TableHead className="text-right">Cache read</TableHead>
            <TableHead className="text-right">Cache write</TableHead>
            <TableHead className="text-right">Output</TableHead>
            <TableHead className="text-right">Messages</TableHead>
            <TableHead className="text-right">Unpriced</TableHead>
            <TableHead className="text-right">USD</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={`${line.workload}:${line.model_id}`}>
              <TableCell className="font-mono">
                {line.model_id === '' ? (
                  // The ledger could not name the model — or the line is
                  // compute, which has none. Said in words rather than left
                  // blank, so an empty cell cannot read as a missing value.
                  <span className="font-sans italic text-muted-foreground">unresolved model</span>
                ) : (
                  line.model_id
                )}
              </TableCell>
              <TableCell>{line.workload}</TableCell>
              <TableCell className="text-right tabular-nums">{countText(line.uncached_input_tokens)}</TableCell>
              <TableCell className="text-right tabular-nums">{countText(line.cache_read_tokens)}</TableCell>
              <TableCell className="text-right tabular-nums">{countText(line.cache_write_tokens)}</TableCell>
              <TableCell className="text-right tabular-nums">{countText(line.output_tokens)}</TableCell>
              <TableCell className="text-right tabular-nums">{countText(line.message_count)}</TableCell>
              <TableCell
                className="text-right tabular-nums"
                title={`${countText(line.unpriced_message_count)} of ${countText(line.message_count)} not priced`}
              >
                {percentText(line.unpriced_message_count, line.message_count)}
              </TableCell>
              <TableCell className="text-right font-medium tabular-nums">{money(line.usd, 'USD')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * The CSV of the range on screen, fetched with the bearer and saved.
 *
 * Carries the same selection the screen was read with, so the file holds
 * exactly the days above it. Its own component because it needs the session's
 * `authorizedFetch` and its own in-flight and failed states: a refusal is
 * shown beside the button in the service's words rather than swallowed, since
 * a download that silently did nothing is the thing an operator reports as
 * broken.
 */
function ExportButton({
  range,
  showFormer,
  rangeLabel,
}: {
  range: DayRangeQuery | null;
  showFormer: boolean;
  rangeLabel: string;
}) {
  const { authorizedFetch } = usePortalSession();
  const [state, setState] = useState<{ busy: boolean; failed: string | null }>({
    busy: false,
    failed: null,
  });

  const download = () => {
    setState({ busy: true, failed: null });
    downloadFleetCostsExport(authorizedFetch, range, showFormer)
      .then(() => setState({ busy: false, failed: null }))
      .catch((failure: unknown) =>
        setState({
          busy: false,
          failed:
            failure instanceof PortalRequestFailed
              ? failure.message
              : 'The export could not be downloaded. Check your connection and try again.',
        }),
      );
  };

  return (
    <div className="shrink-0 text-right">
      <Button variant="outline" size="sm" isDisabled={state.busy} onPress={download}>
        <Download className="size-4" /> Export CSV
      </Button>
      <div className="mt-1 text-xs text-muted-foreground">
        {state.failed ?? `${rangeLabel}, one row per instance, day and line`}
      </div>
    </div>
  );
}
